import axios from 'axios'

// Suporta variável específica de produção ou variável genérica
// Prioridade: REACT_APP_API_BASE_URL_PROD > REACT_APP_API_URL > localhost
const API_BASE_URL = 
  process.env.REACT_APP_API_BASE_URL_PROD || 
  process.env.REACT_APP_API_URL || 
  'http://localhost:3000'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor para adicionar token nas requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || ''
    // Se token expirou ou inválido, remover do localStorage
    // MAS apenas redirecionar para login se NÃO for uma rota pública
    if (error.response?.status === 401) {
      const url = requestUrl
      const method = error.config?.method?.toUpperCase() || 'GET'
      
      // Debug: logar a URL para verificar
      console.log('[API Interceptor] Erro 401 na URL:', url, 'Method:', method)
      
      // Rotas que não devem forçar redirect de login no 401
      // GET /api/products e GET /api/products/:id são públicas
      // GET /api/shipping/* são públicas
      // POST /api/auth/login e POST /api/auth/register são públicas
      // Pedidos exigem auth (ownership) — 401 deve ir para login
      // POST /api/stock/incoming/verify-g1-password e create-product fazem autenticação própria
      const isG1Route = url.includes('verify-g1-password') || url.includes('create-product')
      const isPublicRoute = 
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/signup') ||
        url.includes('/shipping') ||
        url.includes('/coupons/validate') ||
        url.includes('/telemetry/events') ||
        isG1Route ||
        (method === 'GET' && (
          url === '/api/products' ||
          /^\/api\/products\/-?\d+$/.test(url) // /api/products/:id (números, incluindo negativos para grades)
        )) ||
        (method === 'POST' && /^\/api\/products\/grids\/\d+\/find-product$/.test(url)) // Busca produto por variações (público para e-commerce)
      
      // Se não for rota pública, limpar autenticação e redirecionar
      if (!isPublicRoute) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        // Verificar se já não está na página de login para evitar loop
        // Usar pathname completo para garantir que não redirecione para raiz
        const currentPath = window.location.pathname
        if (currentPath !== '/login' && currentPath !== '/') {
          // Usar window.location.href com caminho completo para evitar redirecionamento para raiz
          const loginUrl = window.location.origin + '/login'
          window.location.href = loginUrl
        }
      } else {
        // Para rotas públicas (incluindo verificação G.1), NÃO limpar token nem redirecionar
        // Essas rotas fazem autenticação própria e erros 401 são esperados (senha incorreta, etc)
        // O usuário logado deve permanecer logado mesmo se a verificação G.1 falhar
        console.log('[API Interceptor] Rota pública detectada, mantendo usuário logado. URL:', url, 'isG1Route:', isG1Route)
      }
    }

    const status = error.response?.status
    const isExpectedCepMiss =
      status === 404 && /\/api\/shipping\/cep\//.test(requestUrl)
    // 400 em validate de cupom é fluxo esperado (código inválido/expirado/etc.)
    const isExpectedCouponReject =
      status === 400 && /\/api\/coupons\/validate/.test(requestUrl)
    if (!isExpectedCepMiss && !isExpectedCouponReject) {
      console.error('Erro na API:', error.response?.data || error.message)
    }
    const shiftMessage = error.response?.data?.message || ''
    if (status === 403 && /inicie o plantão|plantão está vinculado/i.test(shiftMessage)) {
      window.dispatchEvent(new CustomEvent('wfmed-shift-blocked', { detail: { message: shiftMessage } }))
    }
    return Promise.reject(error)
  }
)

export const groupService = {
  // Grupos públicos (config da loja Next via ERP)
  getPublicGroups: async () => {
    const response = await api.get('/api/public/groups')
    return response.data
  }
}

export const adminService = {
  // Upload de arquivo Excel
  uploadFile: async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/api/admin/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  // Atualizar preço de produto
  updateProductPrice: async (productId, price) => {
    const response = await api.put(`/api/admin/products/${productId}/price`, { price })
    return response.data
  },

  // Alternar status de destaque do produto
  toggleFeaturedProduct: async (productId) => {
    const response = await api.put(`/api/admin/products/${productId}/featured`)
    return response.data
  },

  // Upload de imagem
  uploadImage: async (imageFile) => {
    const formData = new FormData()
    formData.append('image', imageFile)

    const response = await api.post('/api/admin/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  // Atualizar produto completo
  updateProduct: async (productId, productData) => {
    const response = await api.put(`/api/admin/products/${productId}`, productData)
    return response.data
  },

  // Ativar/Desativar produto no e-commerce
  toggleProductActive: async (productId, active) => {
    const response = await api.put(`/api/admin/products/${productId}`, { active })
    return response.data
  },

  // Gerenciamento de imagens de produtos
  getProductImages: async (productId) => {
    const response = await api.get(`/api/admin/products/${productId}/images`)
    return response.data
  },

  addProductImage: async (productId, imageUrl, displayOrder = 0) => {
    const response = await api.post(`/api/admin/products/${productId}/images`, {
      imageUrl,
      displayOrder
    })
    return response.data
  },

  deleteProductImage: async (productId, imageId) => {
    const response = await api.delete(`/api/admin/products/${productId}/images/${imageId}`)
    return response.data
  },

  updateImageOrder: async (productId, imageId, displayOrder) => {
    const response = await api.put(`/api/admin/products/${productId}/images/${imageId}/order`, {
      displayOrder
    })
    return response.data
  },

  // Upload de arquivo Excel com referências
  uploadReferences: async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/api/admin/upload-references', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  // Buscar todas as referências
  getAllReferences: async () => {
    const response = await api.get('/api/admin/references')
    return response.data
  },

  // Buscar referências de um produto
  getProductReferences: async (productId) => {
    const response = await api.get(`/api/admin/products/${productId}/references`)
    return response.data
  }
}

export const authService = {
  // Registro
  register: async (userData) => {
    const { getAnonymousId } = await import('../utils/tracker')
    const response = await api.post('/api/auth/register', {
      ...userData,
      anonymous_id: getAnonymousId(),
    })
    return response.data
  },

  // Login
  login: async (email, password) => {
    const { getAnonymousId } = await import('../utils/tracker')
    const response = await api.post('/api/auth/login', {
      email,
      password,
      anonymous_id: getAnonymousId(),
    })
    return response.data
  },

  googleConfig: async () => {
    const response = await api.get('/api/auth/google')
    return response.data
  },

  loginWithGoogle: async (credential) => {
    const { getAnonymousId } = await import('../utils/tracker')
    const response = await api.post('/api/auth/google', {
      credential,
      anonymous_id: getAnonymousId(),
    })
    return response.data
  },

  // Obter perfil
  getProfile: async () => {
    const response = await api.get('/api/auth/profile')
    return response.data
  },

  getProfileReminder: async () => {
    const response = await api.get('/api/auth/profile-reminder')
    return response.data
  },

  organizations: async () => {
    const response = await api.get('/api/auth/organizations')
    return response.data
  },

  switchOrganization: async (schema) => {
    const response = await api.post('/api/auth/switch-organization', { schema })
    return response.data
  },

  updateProfile: async (data) => {
    const response = await api.put('/api/auth/profile', data)
    return response.data
  },

  uploadAvatar: async (file) => {
    const formData = new FormData()
    formData.append('avatar', file)
    const response = await api.post('/api/auth/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  saveSignature: async (dataUrl) => {
    const blob = await (await fetch(dataUrl)).blob()
    const formData = new FormData()
    formData.append('signature', blob, 'assinatura.png')
    const response = await api.post('/api/auth/signature', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  deleteSignature: async () => {
    const response = await api.delete('/api/auth/signature')
    return response.data
  },
}

export const shippingService = {
  // Aceita (from, to, products) ou um único objeto { from, to, products }
  calculate: async (fromOrPayload, to, products) => {
    const body =
      fromOrPayload &&
      typeof fromOrPayload === 'object' &&
      !Array.isArray(fromOrPayload) &&
      fromOrPayload.from != null
        ? fromOrPayload
        : { from: fromOrPayload, to, products }
    const response = await api.post('/api/shipping/calculate', body)
    return response.data
  },

  calculateCustom: async (fromOrPayload, to, products) => {
    const body =
      fromOrPayload &&
      typeof fromOrPayload === 'object' &&
      !Array.isArray(fromOrPayload) &&
      fromOrPayload.from != null
        ? fromOrPayload
        : { from: fromOrPayload, to, products }
    const response = await api.post('/api/shipping/calculate-custom', body)
    return response.data
  },

  // Buscar informações de CEP
  getCepInfo: async (cep) => {
    try {
      const response = await api.get(`/api/shipping/cep/${cep}`)
      return response.data
    } catch (error) {
      const data = error?.response?.data
      if (data && typeof data === 'object') {
        return data
      }
      throw error
    }
  },

  // Testar conexão com API
  testConnection: async () => {
    const response = await api.get('/api/shipping/test')
    return response.data
  }
}

export const attendanceService = {
  // Listar registros de ponto
  getEmployees: async () => {
    const response = await api.get('/api/attendance/employees')
    return response.data
  },

  getLogs: async (filters = {}) => {
    const response = await api.get('/api/attendance/logs', { params: filters })
    return response.data
  },

  // Buscar registro por ID
  getLogById: async (id) => {
    const response = await api.get(`/api/attendance/logs/${id}`)
    return response.data
  },

  // Criar novo registro de ponto
  createLog: async (data) => {
    const response = await api.post('/api/attendance/logs', data)
    return response.data
  },

  // Ajustar horário de um registro (relatórios / livro ponto)
  patchLog: async (id, data) => {
    const response = await api.patch(`/api/attendance/logs/${id}`, data)
    return response.data
  },

  // Criar registro manual para preencher horário ausente
  createManualLog: async (data) => {
    const response = await api.post('/api/attendance/logs/manual', data)
    return response.data
  },

  // Buscar status atual
  getStatus: async () => {
    const response = await api.get('/api/attendance/status')
    return response.data
  },

  // Buscar estatísticas
  getStats: async (filters = {}) => {
    const response = await api.get('/api/attendance/stats', { params: filters })
    return response.data
  },

  // Listar relatórios mensais
  getReports: async (filters = {}) => {
    const response = await api.get('/api/attendance/reports', { params: filters })
    return response.data
  },

  // Buscar relatório por ID
  getReportById: async (id) => {
    const response = await api.get(`/api/attendance/reports/${id}`)
    return response.data
  },

  // Gerar relatório mensal
  generateReport: async (data) => {
    const response = await api.post('/api/attendance/reports/generate', data)
    return response.data
  },

  // Buscar períodos disponíveis com registros de ponto
  getAvailablePeriods: async () => {
    const response = await api.get('/api/attendance/reports/available-periods')
    return response.data
  },

  // Enviar relatório para contabilidade
  sendReport: async (id) => {
    const response = await api.post(`/api/attendance/reports/${id}/send`)
    return response.data
  },
  
  // Atestados médicos
  getMedicalCertificates: async (filters) => {
    const response = await api.get('/api/attendance/medical-certificates', { params: filters })
    return response.data
  },
  
  saveMedicalCertificate: async (data) => {
    const response = await api.post('/api/attendance/medical-certificates', data)
    return response.data
  },
  
  deleteMedicalCertificate: async (id) => {
    const response = await api.delete(`/api/attendance/medical-certificates/${id}`)
    return response.data
  }
}

export const fiscalService = {
  // Emitir NFC-e para uma venda
  emitirNFCe: async (saleId, cpf = null) => {
    const payload = { saleId }
    if (cpf) {
      payload.cpf = cpf.replace(/\D/g, '') // Enviar apenas números
    }
    const response = await api.post('/api/v1/fiscal/emitir-nfce', payload)
    return response.data
  },

  // Consultar status de uma NFC-e
  // Listar vendas de um caixa com NFC-e
  listarVendasCaixa: async (caixaId) => {
    const url = `/api/sales/caixa/${caixaId}/nfce`
    console.log('=== listarVendasCaixa - Frontend ===')
    console.log('Caixa ID:', caixaId)
    console.log('URL:', url)
    console.log('Base URL da API:', api.defaults.baseURL)
    const response = await api.get(url)
    console.log('Response:', response.status, response.data)
    return response.data
  },

  // Consultar status de NFC-e
  consultarStatus: async (saleId = null, chaveNfe = null) => {
    const payload = {}
    if (saleId) {
      payload.saleId = saleId
    }
    if (chaveNfe) {
      payload.chaveNfe = chaveNfe.replace(/\D/g, '') // Enviar apenas números
    }
    const response = await api.post('/api/v1/fiscal/consultar-status', payload)
    return response.data
  },

  // Reemitir NFC-e com erro
  reemitirNFCe: async (chaveAcesso) => {
    const response = await api.post('/api/v1/fiscal/reemitir-nfce', { chaveAcesso })
    return response.data
  },

  // Cancelar NFC-e (requer autorização)
  cancelarNFCe: async (saleId, justificativa, usuarioAutorizacao, senhaAutorizacao) => {
    const response = await api.post('/api/v1/fiscal/cancelar-nfce', {
      saleId,
      justificativa,
      usuario_autorizacao: usuarioAutorizacao,
      senha_autorizacao: senhaAutorizacao
    })
    return response.data
  },

  // Imprimir cupom fiscal (NFC-e)
  imprimirCupomFiscal: async (saleId, largura = '80mm') => {
    const response = await api.get(`/api/v1/fiscal/imprimir-nfce/${saleId}`, {
      params: { largura }
    })
    return response.data
  },

  // Imprimir cupom simples (não fiscal)
  imprimirCupomSimples: async (saleId, largura = '80mm') => {
    const response = await api.get(`/api/v1/fiscal/imprimir-cupom-simples/${saleId}`, {
      params: { largura }
    })
    return response.data
  }
}

export const orderService = {
  // Listar todos os pedidos (ERP / vendas online)
  getOrders: async (filters = {}) => {
    const response = await api.get('/api/orders', { params: filters })
    return response.data
  },

  // Pedidos Em Separação para pré-venda
  getForPresale: async (search = '') => {
    const response = await api.get('/api/orders/presale/available', {
      params: search ? { search } : {}
    })
    return response.data
  },

  // Buscar pedido por ID (requer autenticação)
  getOrderById: async (id) => {
    const response = await api.get(`/api/orders/${id}`)
    return response.data
  },

  // Cupom simples vinculado ao pedido online
  getCupom: async (id, largura = '80mm') => {
    const response = await api.get(`/api/orders/${id}/cupom`, {
      params: { largura }
    })
    return response.data
  },

  // Cupom fiscal NFC-e vinculado ao pedido online
  getNfce: async (id) => {
    const response = await api.get(`/api/orders/${id}/nfce`)
    return response.data
  },

  // Atualizar status do pedido (requer autenticação)
  updateOrderStatus: async (id, status) => {
    const response = await api.patch(`/api/orders/${id}/status`, { status })
    return response.data
  },

  // Cancelar pedido (requer autenticação)
  cancelOrder: async (id, reason, options = {}) => {
    const response = await api.delete(`/api/orders/${id}`, {
      data: { reason, refund: options.refund !== false }
    })
    return response.data
  }
}

export default api

